import { useEffect, useState } from "react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { ShieldOff, Unlock, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const formatApiError = (err) =>
  err?.response?.data?.detail || err?.message || "Request failed";

/**
 * Blocked Staff panel — surfaces every employee whose attendance is currently
 * gated by the achievement framework (NOT just sales). Lets Super Admin or
 * Management unblock individuals for N days with a mandatory reason.
 */
export default function BlockedStaffPanel({ canUnblock }) {
  const [data, setData] = useState({ blocked: [], at_risk: [], blocked_count: 0, at_risk_count: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [includeAtRisk, setIncludeAtRisk] = useState(false);
  const [unblockDialog, setUnblockDialog] = useState({ open: false, row: null });
  const [unblockForm, setUnblockForm] = useState({ reason: "" });
  const [submitting, setSubmitting] = useState(false);

  const fetchBlocked = async (showSpinner = true) => {
    if (showSpinner) setLoading(true); else setRefreshing(true);
    try {
      const res = await api.get("/attendance/blocked-staff", {
        params: { include_at_risk: includeAtRisk },
      });
      setData(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchBlocked();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeAtRisk]);

  const openUnblock = (row) => {
    setUnblockForm({ reason: "" });
    setUnblockDialog({ open: true, row });
  };

  const submitUnblock = async () => {
    const row = unblockDialog.row;
    if (!row) return;
    if (!(unblockForm.reason || "").trim()) {
      toast.error("Reason is required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/attendance/unblock/${row.employee_id}`, {
        reason: unblockForm.reason.trim(),
      });
      toast.success(`${row.employee_name} unblocked — counter reset`);
      setUnblockDialog({ open: false, row: null });
      fetchBlocked(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const revokeUnblock = async (row) => {
    if (!window.confirm(`Revoke active unblock for ${row.employee_name}?`)) return;
    try {
      await api.delete(`/attendance/unblock/${row.employee_id}`);
      toast.success("Grant revoked");
      fetchBlocked(false);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Card className="border border-red-200 shadow-sm rounded-md overflow-hidden" data-testid="blocked-staff-panel">
      <CardHeader className="pb-2 bg-red-50/40">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm font-semibold text-red-800 flex items-center gap-2">
            <ShieldOff className="h-4 w-4" />
            Attendance Blocks
            <Badge variant="secondary" className="ml-2 bg-red-100 text-red-700">
              {data.blocked_count} blocked
            </Badge>
            {includeAtRisk && (
              <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                {data.at_risk_count} at-risk
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-1.5 text-xs text-neutral-600 cursor-pointer">
              <input
                type="checkbox"
                checked={includeAtRisk}
                onChange={(e) => setIncludeAtRisk(e.target.checked)}
                data-testid="include-at-risk-toggle"
              />
              Include at-risk (1-day warning)
            </label>
            <Button
              size="sm"
              variant="outline"
              className="h-8 rounded-sm"
              onClick={() => fetchBlocked(false)}
              disabled={refreshing}
              data-testid="refresh-blocked-staff"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>

      {loading ? (
        <div className="p-6 text-sm text-neutral-500">Loading…</div>
      ) : data.blocked.length === 0 && (!includeAtRisk || data.at_risk.length === 0) ? (
        <div className="p-6 text-center text-sm text-neutral-500 flex items-center justify-center gap-2">
          <ShieldCheck className="h-4 w-4 text-emerald-500" />
          No staff are currently blocked.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-neutral-50">
                <TableHead className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">Status</TableHead>
                <TableHead className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">Name</TableHead>
                <TableHead className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 hidden md:table-cell">Branch</TableHead>
                <TableHead className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 hidden md:table-cell">Role</TableHead>
                <TableHead className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 hidden sm:table-cell">Days Missed</TableHead>
                <TableHead className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 hidden lg:table-cell">Last Submission</TableHead>
                <TableHead className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...data.blocked, ...(includeAtRisk ? data.at_risk : [])].map((row) => {
                const isRed = row.status === "red";
                return (
                  <TableRow
                    key={row.employee_id}
                    className={isRed ? "bg-red-50/40" : "bg-amber-50/40"}
                    data-testid={`blocked-row-${row.employee_id}`}
                  >
                    <TableCell>
                      <Badge variant="secondary" className={isRed ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}>
                        {isRed ? "Blocked" : "At Risk"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm font-medium text-neutral-900">
                      {row.employee_name}
                      <p className="text-[11px] text-neutral-500 font-normal">
                        {row.employee_code || "-"}
                      </p>
                    </TableCell>
                    <TableCell className="text-sm text-neutral-700 hidden md:table-cell">
                      {row.branch_name}
                    </TableCell>
                    <TableCell className="text-xs text-neutral-600 hidden md:table-cell">
                      {row.job_role || "-"}
                    </TableCell>
                    <TableCell className="text-sm text-neutral-700 hidden sm:table-cell">
                      {row.days_missing}
                    </TableCell>
                    <TableCell className="text-xs text-neutral-500 hidden lg:table-cell">
                      {row.last_submission_date || "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {canUnblock ? (
                        row.unblock_grant ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 text-xs text-orange-700"
                            onClick={() => revokeUnblock(row)}
                            data-testid={`revoke-unblock-${row.employee_id}`}
                          >
                            Revoke Grant
                          </Button>
                        ) : (
                          isRed && (
                            <Button
                              size="sm"
                              className="h-8 bg-red-600 hover:bg-red-700 text-white text-xs"
                              onClick={() => openUnblock(row)}
                              data-testid={`unblock-${row.employee_id}`}
                            >
                              <Unlock className="h-3.5 w-3.5 mr-1" />
                              Unblock
                            </Button>
                          )
                        )
                      ) : (
                        <span className="text-xs text-neutral-400">SA / HR only</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Unblock dialog */}
      <Dialog
        open={unblockDialog.open}
        onOpenChange={(o) => setUnblockDialog({ open: o, row: unblockDialog.row })}
      >
        <DialogContent className="max-w-sm" data-testid="unblock-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">
              Unblock {unblockDialog.row?.employee_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-neutral-600">
              Resets the missed-day counter. The employee can mark attendance immediately.
              They will be blocked again only if they miss <strong>2 consecutive days</strong> of brand achievements after this unblock.
              Audit-logged. The employee is notified.
            </p>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Reason (required)</Label>
              <Textarea
                rows={3}
                value={unblockForm.reason}
                onChange={(e) => setUnblockForm((p) => ({ ...p, reason: e.target.value }))}
                placeholder="e.g. On leave the previous day, system error, etc."
                data-testid="unblock-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={() => setUnblockDialog({ open: false, row: null })}
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white rounded-sm"
              onClick={submitUnblock}
              disabled={submitting}
              data-testid="unblock-confirm"
            >
              {submitting ? "Unblocking…" : "Confirm Unblock"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
